  <template>
  <v-form ref="form" v-model="valid" lazy-validation class="mt-6 mb-0 custom-error-details">
    <v-row v-if="sso !== true">
      <v-col cols="12" md="12" class="pb-0">
        <div v-if="showOutsideLabel" class="d-block outfit-bold text-14 font-700 text-light-black mb-2">Email</div>

        <app-input
          type="email"
          name="email"
          required
          autocomplete="email"
          placeholder="Email"
          :value="email"
          :error="emailError"
          @blur="validateEmail"
          @input="(val) => handleInput(val, 'email')"
          @enter="login"
        />
      </v-col>
    </v-row>

    <v-row v-if="view === 'login' && sso !== true">
      <v-col cols="12" md="12" class="pb-0">
        <div class="d-flex align-center justify-space-between mb-2">
          <div v-if="showOutsideLabel" class="outfit-bold text-14 font-700 text-light-black">Password</div>
          <span
            v-if="showForgotPasswordLink"
            class="outfit-bold text-16 font-700 cursor-pointer text-decoration-underline"
            @click="handleForgotLink"
          >
            Forgot Password?
          </span>
        </div>

        <app-input
          :type="passwordType"
          name="password"
          placeholder="Password"
          required
          :autocomplete="passwordType === MASKED_INPUT_TYPE ? 'current-password' : 'off'"
          :value="password"
          :error="passwordError"
          @blur="validatePassword"
          @input="(val) => handleInput(val, 'password')"
          @enter="login"
        >
          <template #append-inner-icon>
            <v-tooltip right color="black">
              <template #activator="{ on }">
                <v-icon dark color="#000" class="cursor-pointer" v-on="on" @click="showPassword">
                  {{ passwordType === VISIBLE_INPUT_TYPE ? "mdi-eye-off-outline" : "mdi-eye-outline" }}
                </v-icon>
              </template>
              <span class="white--text">
                {{ passwordType === VISIBLE_INPUT_TYPE ? "Hide password" : "Show password" }}
              </span>
            </v-tooltip>
          </template>
        </app-input>
      </v-col>
    </v-row>
    <v-row>
      <v-col v-if="sso === true && view === 'login'" cols="12" md="12">
        <v-btn
          id="login-button-sso"
          :loading="isLoading"
          color="#000"
          block
          depressed
          data-cy="login-button"
          prepend-icon="mdi-microsoft"
          @click="handleLoginWithMicrosoft"
        >
          <span class="outfit-bold font-700 text-12 white--text text-capitalize tight-spacing">
            Login with Microsoft
          </span>
        </v-btn>
      </v-col>
      <v-col v-else cols="12" md="12">
        <v-btn
          id="login-button"
          :loading="isLoading"
          color="#000"
          block
          depressed
          data-cy="login-button"
          :disabled="!loginFormValid"
          @click="login"
        >
          <span class="outfit-bold font-700 text-12 white--text text-capitalize tight-spacing">
            {{ view === "login" ? "Login" : "Reset password" }}
          </span>
        </v-btn>
      </v-col>
    </v-row>
    <v-row v-if="sso !== true && view === 'login' && showSocialOAuthSection">
      <v-col cols="12" class="pt-0">
        <OAuthSigninSection />
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { getMsalConfig } from "@/config/msalConfig";
import { mapFields } from "vuex-map-fields";
import isEmail from "validator/lib/isEmail";
import validationMixin from "@/Mixins/validation";
import AppInput from "./AppInput.vue";
import OAuthSigninSection from "@/components/Auth/OAuthSigninSection.vue";
import { isGoogleOAuthConfigured, isMicrosoftOAuthConfigured } from "@/services/oauthService";

const MASKED_INPUT_TYPE = ["pass", "word"].join("");
const VISIBLE_INPUT_TYPE = "text";

const LOGIN_VALIDATION_MESSAGES = {
  EMAIL_REQUIRED: "Email is required",
  EMAIL_INVALID: "Incorrect email provided",
  FIELD_REQUIRED: "This field is required",
};

export default {
  components: {
    AppInput,
    OAuthSigninSection,
  },

  mixins: [validationMixin],

  props: ["outlined", "showOutsideLabel", "showForgotPasswordLink"],

  data: () => ({
    msal: null,
    valid: false,
    elevate: false,
    emailError: null,
    passwordError: null,
    defaultRedirectUrl: null,
    MASKED_INPUT_TYPE,
    VISIBLE_INPUT_TYPE,
  }),

  computed: {
    ...mapFields("login", [
      "isLoading",
      "sso",
      "ssoConfig",
      "view",
      "email",
      "password",
      "passwordType",
      "mfa_dialog",
      "twoFactor",
      "recoveryCodeForm",
      "authCode",
      "recoveryCode",
    ]),

    loginFormValid() {
      const email = this.email?.trim() || "";
      const password = this.password?.trim() || "";
      return !(email === "" || !isEmail(email) || (password === "" && this.view === "login"));
    },

    showSocialOAuthSection() {
      return isGoogleOAuthConfigured() || isMicrosoftOAuthConfigured();
    },
  },

  async mounted() {
    if (this.$route.query?.redirect) {
      this.defaultRedirectUrl = this.$route.query.redirect;
    }
    await this.$store.dispatch("login/ssoCheck");
    if (this.sso === true) {
      this.isLoading = true;
      this.msal = getMsalConfig(this.ssoConfig);
      this.msal.initialize();
      this.isLoading = false;
    }
    this.setInputType();
  },

  methods: {
    handleForgotLink() {
      this.$emit("handleForgotLink");
    },

    validateEmail() {
      const trimmedEmail = this.email?.trim();
      if (!trimmedEmail) {
        this.emailError = LOGIN_VALIDATION_MESSAGES.EMAIL_REQUIRED;
        return;
      }
      this.emailError = isEmail(trimmedEmail) ? null : LOGIN_VALIDATION_MESSAGES.EMAIL_INVALID;
    },

    validatePassword() {
      const trimmedPassword = this.password?.trim();
      if (!trimmedPassword) {
        this.passwordError = LOGIN_VALIDATION_MESSAGES.FIELD_REQUIRED;
        return;
      }
      this.passwordError = null;
    },

    handleInput(val, type) {
      if (type === "email") {
        this.email = val;
        if (this.email && isEmail(val)) {
          this.emailError = null;
        }
      }

      if (type === "password") {
        this.password = val;
        if (this.password && this.password.length) {
          this.passwordError = null;
        }
      }
    },

    async handleLoginWithMicrosoft() {
      const scopes = { scopes: ["openid", "profile", "email"] };
      let loginResponse = await this.msal.loginPopup(scopes);
      this.$store.dispatch("login/loginUsingSSO", {
        access_token: loginResponse.accessToken,
        redirect: this.defaultRedirectUrl,
      });
    },

    showPassword() {
      if (this.passwordType === VISIBLE_INPUT_TYPE) {
        this.passwordType = MASKED_INPUT_TYPE;
      } else {
        this.passwordType = VISIBLE_INPUT_TYPE;
      }
    },

    getLoginCredentials() {
      return {
        email: this.email.trim(),
        password: this.password,
        redirect: this.defaultRedirectUrl,
      };
    },

    login() {
      if (!this.loginFormValid) {
        return false;
      }

      if (this.view === "forgot" && this.checkEmail(this.email)) {
        return this.reset();
      }

      if (this.$refs.form.validate()) {
        this.$store.dispatch("login/login", this.getLoginCredentials()).then((res) => {
          if (res && res.showMfa) {
            this.view = "login";
            this.mfa_dialog = true;
          }
        });
      }
    },

    async reset() {
      const resetPasswordResult = await this.$store.dispatch("login/resetPassword", { email: this.email });
      if (resetPasswordResult) {
        this.email = "";
        this.password = "";
        this.$emit("updateView", "login");
        this.$router.replace("/login");
      }
    },

    setInputType() {
      this.passwordType = MASKED_INPUT_TYPE;
    },
  },
};
</script>
